import React, { useEffect, useState } from 'react';
import { 
  Shield, 
  Users, 
  Settings, 
  Save, 
  RefreshCw, 
  Eye, 
  EyeOff,
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  Filter,
  Search
} from 'lucide-react';
import { pagePrivilegesAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner,
  ModernSwitch
} from '../components/ui/ModernComponents';
import {
  ResponsiveWrapper,
  ResponsiveSpacing,
  ResponsiveHeader,
  ResponsiveStats,
  ResponsiveCard,
  ResponsiveTableWrapper,
  ResponsiveFormWrapper,
  ResponsiveFormGrid,
  ResponsiveFooter,
  ResponsiveActions,
  ResponsiveSearchWrapper
} from '../components/ui/ResponsiveWrapper';
import { ResponsiveHybridTable } from '../components/ui/ResponsiveTable';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'employee', label: 'Employé' },
];

const PagePrivileges = () => {
  const { isAdmin } = useAuth();
  const [privileges, setPrivileges] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [selectedRole, setSelectedRole] = useState('employee');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPrivilege, setEditingPrivilege] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const [newPrivilege, setNewPrivilege] = useState({
    page_path: '',
    page_name: '',
    page_section: '',
    role: 'employee',
    can_access: true,
    can_create: false,
    can_edit: false,
    can_delete: false
  });

  // Redirect if not admin
  useEffect(() => {
    console.log('PagePrivileges component mounted');
    console.log('isAdmin check:', isAdmin());
    if (!isAdmin()) {
      toast.error('Accès non autorisé');
      window.location.href = '/';
    }
  }, [isAdmin]);

  // Fetch privileges and sections
  const fetchData = async () => {
    setLoading(true);
    try {
      const [privilegesRes, sectionsRes] = await Promise.all([
        pagePrivilegesAPI.getAllPrivileges(),
        pagePrivilegesAPI.getSections()
      ]);
      
      // Convert numeric boolean values to actual booleans
      const convertedPrivileges = privilegesRes.data.map(privilege => {
        const converted = {
          ...privilege,
          can_access: Boolean(privilege.can_access),
          can_create: Boolean(privilege.can_create),
          can_edit: Boolean(privilege.can_edit),
          can_delete: Boolean(privilege.can_delete)
        };
        
        // Debug logging for first few privileges
        if (privilege.id <= 5) {
          console.log('Converting privilege:', privilege.page_name, {
            original: {
              can_access: privilege.can_access,
              can_create: privilege.can_create,
              can_edit: privilege.can_edit,
              can_delete: privilege.can_delete
            },
            converted: {
              can_access: converted.can_access,
              can_create: converted.can_create,
              can_edit: converted.can_edit,
              can_delete: converted.can_delete
            }
          });
        }
        
        return converted;
      });
      
      setPrivileges(convertedPrivileges);
      setSections(sectionsRes.data);
      
      // Debug: Log the first few privileges to verify conversion
      console.log('First 3 privileges after conversion:', convertedPrivileges.slice(0, 3));
    } catch (err) {
      toast.error('Erreur lors du chargement des privilèges');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter privileges
  const filteredPrivileges = privileges.filter(privilege => {
    const matchesSearch = !searchTerm || 
      privilege.page_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      privilege.page_path.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !roleFilter || privilege.role === roleFilter;
    const matchesSection = !sectionFilter || privilege.page_section === sectionFilter;
    
    const matches = matchesSearch && matchesRole && matchesSection;
    
    // Debug logging for filtering
    if (searchTerm || roleFilter || sectionFilter) {
      console.log('Filtering privilege:', privilege.page_name, {
        searchTerm,
        roleFilter,
        sectionFilter,
        matchesSearch,
        matchesRole,
        matchesSection,
        matches
      });
    }
    
    return matches;
  });

  // Group privileges by section and role
  const groupedPrivileges = filteredPrivileges.reduce((acc, privilege) => {
    const key = `${privilege.page_section}-${privilege.role}`;
    if (!acc[key]) {
      acc[key] = {
        section: privilege.page_section,
        role: privilege.role,
        privileges: []
      };
    }
    acc[key].privileges.push(privilege);
    return acc;
  }, {});

  // Sort groups by section and role
  const sortedGroups = Object.values(groupedPrivileges).sort((a, b) => {
    if (a.section !== b.section) {
      return a.section.localeCompare(b.section);
    }
    const roleOrder = { admin: 0, manager: 1, employee: 2 };
    return roleOrder[a.role] - roleOrder[b.role];
  });

  // Handle add new privilege
  const handleAddPrivilege = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      const privilegeData = {
        ...newPrivilege,
        can_access: newPrivilege.can_access ? 1 : 0,
        can_create: newPrivilege.can_create ? 1 : 0,
        can_edit: newPrivilege.can_edit ? 1 : 0,
        can_delete: newPrivilege.can_delete ? 1 : 0
      };
      await pagePrivilegesAPI.createPrivilege(privilegeData);
      toast.success('Privilège ajouté avec succès');
      setShowAddModal(false);
      setNewPrivilege({
        page_path: '',
        page_name: '',
        page_section: '',
        role: 'employee',
        can_access: true,
        can_create: false,
        can_edit: false,
        can_delete: false
      });
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de l\'ajout du privilège');
    }
    setSubmitLoading(false);
  };

  // Handle edit privilege
  const handleEditPrivilege = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      await pagePrivilegesAPI.updatePrivilege(editingPrivilege.id, {
        can_access: editingPrivilege.can_access ? 1 : 0,
        can_create: editingPrivilege.can_create ? 1 : 0,
        can_edit: editingPrivilege.can_edit ? 1 : 0,
        can_delete: editingPrivilege.can_delete ? 1 : 0
      });
      toast.success('Privilège modifié avec succès');
      setShowEditModal(false);
      setEditingPrivilege(null);
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la modification du privilège');
    }
    setSubmitLoading(false);
  };

  // Handle delete privilege
  const handleDeletePrivilege = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce privilège ?')) {
      try {
        await pagePrivilegesAPI.deletePrivilege(id);
        toast.success('Privilège supprimé avec succès');
        fetchData();
      } catch (err) {
        toast.error('Erreur lors de la suppression du privilège');
      }
    }
  };

  // Handle bulk update for a role
  const handleBulkUpdate = async (role) => {
    setBulkUpdating(true);
    try {
      const rolePrivileges = privileges.filter(p => p.role === role).map(privilege => ({
        ...privilege,
        can_access: privilege.can_access ? 1 : 0,
        can_create: privilege.can_create ? 1 : 0,
        can_edit: privilege.can_edit ? 1 : 0,
        can_delete: privilege.can_delete ? 1 : 0
      }));
      await pagePrivilegesAPI.bulkUpdatePrivileges(role, rolePrivileges);
      toast.success(`Privilèges mis à jour pour le rôle ${role}`);
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour en masse');
    }
    setBulkUpdating(false);
  };

  // Handle privilege toggle
  const handlePrivilegeToggle = (privilegeId, field, value) => {
    setPrivileges(prev => prev.map(p => 
      p.id === privilegeId ? { ...p, [field]: Boolean(value) } : p
    ));
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'manager': return 'primary';
      case 'employee': return 'success';
      default: return 'neutral';
    }
  };

  // Get privilege status badge
  const getPrivilegeStatusBadge = (privilege) => {
    if (!privilege.can_access) {
      return <ModernBadge variant="danger">Accès refusé</ModernBadge>;
    }
    
    const permissions = [];
    if (privilege.can_create) permissions.push('Créer');
    if (privilege.can_edit) permissions.push('Modifier');
    if (privilege.can_delete) permissions.push('Supprimer');
    
    if (permissions.length === 0) {
      return <ModernBadge variant="warning">Lecture seule</ModernBadge>;
    }
    
    return <ModernBadge variant="success">{permissions.join(', ')}</ModernBadge>;
  };

  if (loading) {
    console.log('PagePrivileges: Loading state');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  console.log('PagePrivileges: Rendered with', privileges.length, 'privileges');
  console.log('Sample privilege:', privileges[0]);
  
  // Test: Check if we have any admin privileges with can_access = true
  const adminPrivileges = privileges.filter(p => p.role === 'admin' && p.can_access);
  console.log('Admin privileges with access:', adminPrivileges.length);
  console.log('First admin privilege:', adminPrivileges[0]);

  const stats = {
    total: privileges.length,
    byRole: {
      admin: privileges.filter(p => p.role === 'admin').length,
      manager: privileges.filter(p => p.role === 'manager').length,
      employee: privileges.filter(p => p.role === 'employee').length
    },
    accessible: privileges.filter(p => p.can_access).length,
    restricted: privileges.filter(p => !p.can_access).length
  };

  return (
    <ResponsiveWrapper>
      <ResponsiveSpacing>
        {/* Header */}
        <ResponsiveHeader>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">Gestion des privilèges de pages</h1>
            <p className="text-sm sm:text-base text-neutral-600">Configurez les accès aux pages pour chaque rôle</p>
          </div>
          <div className="flex gap-2">
            <ModernButton
              variant="secondary"
              icon={<RefreshCw className="w-4 h-4" />}
              onClick={fetchData}
            >
              Actualiser
            </ModernButton>
            <ModernButton
              variant="primary"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => setShowAddModal(true)}
            >
              Ajouter un privilège
            </ModernButton>
          </div>
        </ResponsiveHeader>

        {/* Stats Cards */}
        <ResponsiveStats>
          <ResponsiveCard>
            <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-medium text-neutral-600">Total des privilèges</p>
                <p className="text-xl sm:text-2xl font-bold text-neutral-900">{stats.total}</p>
              </div>
            </div>
          </ResponsiveCard>

          <ResponsiveCard>
            <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-success-100 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-success-600" />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-medium text-neutral-600">Pages accessibles</p>
                <p className="text-xl sm:text-2xl font-bold text-success-600">{stats.accessible}</p>
              </div>
            </div>
          </ResponsiveCard>

          <ResponsiveCard>
            <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-warning-100 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                <EyeOff className="w-5 h-5 sm:w-6 sm:h-6 text-warning-600" />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-medium text-neutral-600">Pages restreintes</p>
                <p className="text-xl sm:text-2xl font-bold text-warning-600">{stats.restricted}</p>
              </div>
            </div>
          </ResponsiveCard>
        </ResponsiveStats>

        {/* Filters */}
        <ResponsiveCard>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <ModernSearchBar
              placeholder="Rechercher des pages..."
              onSearch={setSearchTerm}
            />
            <ModernSelect
              label="Rôle"
              value={roleFilter ? { value: roleFilter, label: roleOptions.find(opt => opt.value === roleFilter)?.label } : null}
              onChange={(selectedOption) => setRoleFilter(selectedOption ? selectedOption.value : '')}
              options={[{ value: '', label: 'Tous les rôles' }, ...roleOptions]}
            />
            <ModernSelect
              label="Section"
              value={sectionFilter ? { value: sectionFilter, label: sectionFilter } : null}
              onChange={(selectedOption) => setSectionFilter(selectedOption ? selectedOption.value : '')}
              options={[{ value: '', label: 'Toutes les sections' }, ...sections.map(s => ({ value: s, label: s }))]}
            />
            <div className="flex items-end">
              <ModernButton
                variant="secondary"
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('');
                  setSectionFilter('');
                  console.log('Filters reset');
                }}
              >
                Réinitialiser
              </ModernButton>
            </div>
          </div>
        </ResponsiveCard>

        {/* Privileges by Role */}
        {sortedGroups.map((group) => (
          <ResponsiveCard key={`${group.section}-${group.role}`}>
            <ResponsiveHeader className="mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-neutral-900">{group.section}</h2>
                <ModernBadge variant={getRoleBadgeColor(group.role)}>
                  {roleOptions.find(opt => opt.value === group.role)?.label}
                </ModernBadge>
              </div>
              <ModernButton
                variant="primary"
                size="sm"
                icon={<Save className="w-4 h-4" />}
                onClick={() => handleBulkUpdate(group.role)}
                disabled={bulkUpdating}
              >
                {bulkUpdating ? 'Sauvegarde...' : 'Sauvegarder'}
              </ModernButton>
            </ResponsiveHeader>

            <div className="space-y-4">
              {group.privileges.map((privilege) => (
                <div key={privilege.id} className="border border-neutral-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-neutral-900">{privilege.page_name}</h3>
                      <p className="text-sm text-neutral-500">{privilege.page_path}</p>
                    </div>
                                         <div className="flex items-center gap-2">
                       {getPrivilegeStatusBadge(privilege)}
                     </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral-700">Accès</span>
                      {(() => {
                        console.log('Rendering switch for privilege:', privilege.id, privilege.page_name, 'can_access:', privilege.can_access);
                        return (
                          <ModernSwitch
                            checked={privilege.can_access}
                            onChange={(checked) => {
                              console.log('Toggle access:', privilege.id, privilege.page_name, checked, 'Current value:', privilege.can_access);
                              handlePrivilegeToggle(privilege.id, 'can_access', checked);
                            }}
                          />
                        );
                      })()}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral-700">Créer</span>
                      <ModernSwitch
                        checked={privilege.can_create}
                        onChange={(checked) => {
                          console.log('Toggle create:', privilege.id, checked);
                          handlePrivilegeToggle(privilege.id, 'can_create', checked);
                        }}
                        disabled={!privilege.can_access}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral-700">Modifier</span>
                      <ModernSwitch
                        checked={privilege.can_edit}
                        onChange={(checked) => {
                          console.log('Toggle edit:', privilege.id, checked);
                          handlePrivilegeToggle(privilege.id, 'can_edit', checked);
                        }}
                        disabled={!privilege.can_access}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral-700">Supprimer</span>
                      <ModernSwitch
                        checked={privilege.can_delete}
                        onChange={(checked) => {
                          console.log('Toggle delete:', privilege.id, checked);
                          handlePrivilegeToggle(privilege.id, 'can_delete', checked);
                        }}
                        disabled={!privilege.can_access}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ResponsiveCard>
        ))}

        {/* Add Privilege Modal */}
        <ModernModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          title="Ajouter un privilège"
          size="lg"
        >
          <ResponsiveFormWrapper>
            <form onSubmit={handleAddPrivilege}>
              <ResponsiveFormGrid cols={2}>
                <ModernInput
                  label="Chemin de la page"
                  name="page_path"
                  value={newPrivilege.page_path}
                  onChange={(e) => setNewPrivilege(prev => ({ ...prev, page_path: e.target.value }))}
                  placeholder="/example/page"
                  required
                />
                <ModernInput
                  label="Nom de la page"
                  name="page_name"
                  value={newPrivilege.page_name}
                  onChange={(e) => setNewPrivilege(prev => ({ ...prev, page_name: e.target.value }))}
                  placeholder="Nom de la page"
                  required
                />
                <ModernSelect
                  label="Section"
                  value={newPrivilege.page_section ? { value: newPrivilege.page_section, label: newPrivilege.page_section } : null}
                  onChange={(selectedOption) => setNewPrivilege(prev => ({ ...prev, page_section: selectedOption ? selectedOption.value : '' }))}
                  options={sections.map(s => ({ value: s, label: s }))}
                  placeholder="Sélectionner une section"
                  required
                />
                <ModernSelect
                  label="Rôle"
                  value={newPrivilege.role ? { value: newPrivilege.role, label: roleOptions.find(opt => opt.value === newPrivilege.role)?.label } : null}
                  onChange={(selectedOption) => setNewPrivilege(prev => ({ ...prev, role: selectedOption ? selectedOption.value : '' }))}
                  options={roleOptions}
                  required
                />
              </ResponsiveFormGrid>

              <div className="mt-6 space-y-4">
                <h4 className="font-semibold text-neutral-900">Permissions</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-neutral-700">Accès</span>
                    <ModernSwitch
                      checked={newPrivilege.can_access}
                      onChange={(checked) => setNewPrivilege(prev => ({ ...prev, can_access: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-neutral-700">Créer</span>
                    <ModernSwitch
                      checked={newPrivilege.can_create}
                      onChange={(checked) => setNewPrivilege(prev => ({ ...prev, can_create: checked }))}
                      disabled={!newPrivilege.can_access}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-neutral-700">Modifier</span>
                    <ModernSwitch
                      checked={newPrivilege.can_edit}
                      onChange={(checked) => setNewPrivilege(prev => ({ ...prev, can_edit: checked }))}
                      disabled={!newPrivilege.can_access}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-neutral-700">Supprimer</span>
                    <ModernSwitch
                      checked={newPrivilege.can_delete}
                      onChange={(checked) => setNewPrivilege(prev => ({ ...prev, can_delete: checked }))}
                      disabled={!newPrivilege.can_access}
                    />
                  </div>
                </div>
              </div>

              <ResponsiveFooter className="pt-4">
                <ModernButton
                  type="button"
                  variant="secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Annuler
                </ModernButton>
                <ModernButton
                  type="submit"
                  variant="primary"
                  icon={<Save className="w-4 h-4" />}
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Ajout...' : 'Ajouter'}
                </ModernButton>
              </ResponsiveFooter>
            </form>
          </ResponsiveFormWrapper>
        </ModernModal>

        {/* Edit Privilege Modal */}
        <ModernModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Modifier le privilège"
          size="lg"
        >
          {editingPrivilege && (
            <ResponsiveFormWrapper>
              <form onSubmit={handleEditPrivilege}>
                <div className="mb-6">
                  <h3 className="font-semibold text-neutral-900 mb-2">{editingPrivilege.page_name}</h3>
                  <p className="text-sm text-neutral-500">{editingPrivilege.page_path}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm text-neutral-600">Section:</span>
                    <ModernBadge variant="neutral">{editingPrivilege.page_section}</ModernBadge>
                    <span className="text-sm text-neutral-600">Rôle:</span>
                    <ModernBadge variant={getRoleBadgeColor(editingPrivilege.role)}>
                      {roleOptions.find(opt => opt.value === editingPrivilege.role)?.label}
                    </ModernBadge>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-neutral-900">Permissions</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral-700">Accès</span>
                      <ModernSwitch
                        checked={editingPrivilege.can_access}
                        onChange={(checked) => setEditingPrivilege(prev => ({ ...prev, can_access: checked }))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral-700">Créer</span>
                      <ModernSwitch
                        checked={editingPrivilege.can_create}
                        onChange={(checked) => setEditingPrivilege(prev => ({ ...prev, can_create: checked }))}
                        disabled={!editingPrivilege.can_access}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral-700">Modifier</span>
                      <ModernSwitch
                        checked={editingPrivilege.can_edit}
                        onChange={(checked) => setEditingPrivilege(prev => ({ ...prev, can_edit: checked }))}
                        disabled={!editingPrivilege.can_access}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral-700">Supprimer</span>
                      <ModernSwitch
                        checked={editingPrivilege.can_delete}
                        onChange={(checked) => setEditingPrivilege(prev => ({ ...prev, can_delete: checked }))}
                        disabled={!editingPrivilege.can_access}
                      />
                    </div>
                  </div>
                </div>

                <ResponsiveFooter className="pt-4">
                  <ModernButton
                    type="button"
                    variant="secondary"
                    onClick={() => setShowEditModal(false)}
                  >
                    Annuler
                  </ModernButton>
                  <ModernButton
                    type="submit"
                    variant="primary"
                    icon={<Save className="w-4 h-4" />}
                    disabled={submitLoading}
                  >
                    {submitLoading ? 'Modification...' : 'Modifier'}
                  </ModernButton>
                </ResponsiveFooter>
              </form>
            </ResponsiveFormWrapper>
          )}
        </ModernModal>
      </ResponsiveSpacing>
    </ResponsiveWrapper>
  );
};

export default PagePrivileges;
